const HeatmapConfig = {
  defaultRadius: 40,
  defaultRenderer: 'canvas2d',
  defaultGradient: { 0.25: 'rgb(0,0,255)', 0.55: 'rgb(0,255,0)', 0.85: 'yellow', 1.0: 'rgb(255,0,0)' },
  defaultMaxOpacity: 1,
  defaultMinOpacity: 0,
  defaultBlur: 0.85,
  defaultXField: 'x',
  defaultYField: 'y',
  defaultValueField: 'value',
  plugins: {},
}

interface IPoint {
  x: number
  y: number
  value: number
  radius: number
  min?: number
  max?: number
}

interface IConfig {
  radius?: number
  xField?: string
  yField?: string
  valueField?: string
  defaultXField: string
  defaultYField: string
  defaultValueField: string
}

class Store {
  private _coordinator: any = {}
  private _data: any[] = []
  private _radi: any[] = []
  private _min = 10
  private _max = 1
  private _xField: string
  private _yField: string
  private _valueField: string
  private _cfgRadius?: number

  constructor(config: IConfig) {
    this._xField = config.xField || config.defaultXField
    this._yField = config.yField || config.defaultYField
    this._valueField = config.valueField || config.defaultValueField

    if (config.radius)
      this._cfgRadius = config.radius
  }

  private _organiseData(dataPoint: any, forceRender: boolean): false | IPoint {
    const x = dataPoint[this._xField]
    const y = dataPoint[this._yField]
    const radi = this._radi
    const store = this._data
    const max = this._max
    const min = this._min
    const value = dataPoint[this._valueField] || 1
    const radius = dataPoint.radius || this._cfgRadius || HeatmapConfig.defaultRadius

    if (!store[x]) {
      store[x] = []
      radi[x] = []
    }

    if (!store[x][y]) {
      store[x][y] = value
      radi[x][y] = radius
    }
    else {
      store[x][y] += value
    }
    const storedVal = store[x][y]

    if (storedVal > max) {
      if (!forceRender)
        this._max = storedVal
      else
        this.setDataMax(storedVal)

      return false
    }
    else if (storedVal < min) {
      if (!forceRender)
        this._min = storedVal
      else
        this.setDataMin(storedVal)

      return false
    }
    else {
      return {
        x,
        y,
        value,
        radius,
        min,
        max,
      }
    }
  }

  private _unOrganizeData() {
    const unorganizedData = []
    const data = this._data
    const radi = this._radi

    for (const x in data) {
      for (const y in data[x]) {
        unorganizedData.push({
          x,
          y,
          radius: radi[x][y],
          value: data[x][y],
        })
      }
    }
    return {
      min: this._min,
      max: this._max,
      data: unorganizedData,
    }
  }

  private _onExtremaChange() {
    this._coordinator.emit('extremachange', {
      min: this._min,
      max: this._max,
    })
  }

  addData(...args: any[]) {
    if (args[0].length > 0) {
      const dataArr = args[0]
      let dataLen = dataArr.length
      while (dataLen--)
        this.addData(dataArr[dataLen])
    }
    else {
      // add to store
      const organisedEntry = this._organiseData(args[0], true)
      if (organisedEntry) {
        // if it's the first datapoint initialize the extremas with it
        if (this._data.length === 0)
          this._min = this._max = organisedEntry.value

        this._coordinator.emit('renderpartial', {
          min: this._min,
          max: this._max,
          data: [organisedEntry],
        })
      }
    }
    return this
  }

  setData(data: any) {
    const dataPoints = data.data
    const pointsLen = dataPoints.length

    // reset data arrays
    this._data = []
    this._radi = []

    for (let i = 0; i < pointsLen; i++)
      this._organiseData(dataPoints[i], false)

    this._max = data.max
    this._min = data.min || 0

    this._onExtremaChange()
    this._coordinator.emit('renderall', this.getInternalData())
    return this
  }

  removeData() {
    // TODO: implement
  }

  setDataMax(max: number) {
    this._max = max
    this._onExtremaChange()
    this._coordinator.emit('renderall', this.getInternalData())
    return this
  }

  setDataMin(min: number) {
    this._min = min
    this._onExtremaChange()
    this._coordinator.emit('renderall', this.getInternalData())
    return this
  }

  setCoordinator(coordinator: any) {
    this._coordinator = coordinator
  }

  getInternalData() {
    return {
      max: this._max,
      min: this._min,
      data: this._data,
      radi: this._radi,
    }
  }

  getData() {
    return this._unOrganizeData()
  }

  getValueAt(point: { x: number; y: number }) {
    const shadowCtx = (this as any)._renderer?.shadowCtx
    if (!shadowCtx)
      return null

    const img = shadowCtx.getImageData(point.x, point.y, 1, 1)
    const data = img.data[3]
    const max = this._max
    const min = this._min

    const value = (Math.abs(max! - min!) * (data / 255)) >> 0
    return value
  }
}

class Canvas2dRenderer {
  private shadowCanvas: HTMLCanvasElement
  private canvas: HTMLCanvasElement
  private _renderBoundaries: number[]
  private _width: number
  private _height: number
  private shadowCtx: CanvasRenderingContext2D
  private ctx: CanvasRenderingContext2D
  private _palette: Uint8ClampedArray
  private _templates: { [key: string]: HTMLCanvasElement }
  private _blur: number
  private _opacity: number
  private _maxOpacity: number
  private _minOpacity: number
  private _useGradientOpacity: boolean
  private _min?: number
  private _max?: number

  constructor(config: any) {
    const container = config.container
    this.shadowCanvas = document.createElement('canvas')
    this.canvas = config.canvas || document.createElement('canvas')
    this._renderBoundaries = [10000, 10000, 0, 0]

    const computed = getComputedStyle(config.container) || {}

    this.canvas.className = 'heatmap-canvas'

    this._width = this.canvas.width = this.shadowCanvas.width = config.width || +(computed as any).width?.replace(/px/, '') || 0
    this._height = this.canvas.height = this.shadowCanvas.height = config.height || +(computed as any).height?.replace(/px/, '') || 0

    this.shadowCtx = this.shadowCanvas.getContext('2d')!
    this.ctx = this.canvas.getContext('2d')!

    this.canvas.style.cssText = this.shadowCanvas.style.cssText = 'position:absolute;left:0;top:0;'

    container.style.position = 'relative'
    container.appendChild(this.canvas)

    this._palette = this._getColorPalette(config)
    this._templates = {}

    this._setStyles(config)
  }

  private _getColorPalette(config: any) {
    const gradientConfig = config.gradient ?? config.defaultGradient
    const paletteCanvas = document.createElement('canvas')
    const paletteCtx = paletteCanvas.getContext('2d')!

    paletteCanvas.width = 256
    paletteCanvas.height = 1

    const gradient = paletteCtx.createLinearGradient(0, 0, 256, 1)
    for (const key in gradientConfig)
      gradient.addColorStop(+key, gradientConfig[key])

    paletteCtx.fillStyle = gradient
    paletteCtx.fillRect(0, 0, 256, 1)

    return paletteCtx.getImageData(0, 0, 256, 1).data
  }

  private _getPointTemplate(radius: number, blurFactor: number) {
    const tplCanvas = document.createElement('canvas')
    const tplCtx = tplCanvas.getContext('2d')!
    const x = radius
    const y = radius
    tplCanvas.width = tplCanvas.height = radius * 2

    if (blurFactor === 1) {
      tplCtx.beginPath()
      tplCtx.arc(x, y, radius, 0, 2 * Math.PI, false)
      tplCtx.fillStyle = 'rgba(0,0,0,1)'
      tplCtx.fill()
    }
    else {
      const gradient = tplCtx.createRadialGradient(x, y, radius * blurFactor, x, y, radius)
      gradient.addColorStop(0, 'rgba(0,0,0,1)')
      gradient.addColorStop(1, 'rgba(0,0,0,0)')
      tplCtx.fillStyle = gradient
      tplCtx.fillRect(0, 0, 2 * radius, 2 * radius)
    }

    return tplCanvas
  }

  private _prepareData(data: any) {
    const renderData = []
    const min = data.min
    const max = data.max
    const radi = data.radi
    const data2 = data.data

    const xValues = Object.keys(data2)
    let xValuesLen = xValues.length

    while (xValuesLen--) {
      const xValue = xValues[xValuesLen]
      const yValues = Object.keys(data2[xValue])
      let yValuesLen = yValues.length
      while (yValuesLen--) {
        const yValue = yValues[yValuesLen]
        const value = data2[xValue][yValue]
        const radius = radi[xValue][yValue]
        renderData.push({
          x: xValue,
          y: yValue,
          value,
          radius,
        })
      }
    }

    return {
      min,
      max,
      data: renderData,
    }
  }

  renderPartial(data: any) {
    if (data.data.length > 0) {
      this._drawAlpha(data)
      this._colorize()
    }
  }

  renderAll(data: any) {
    this._clear()
    if (data.data.length > 0) {
      this._drawAlpha(this._prepareData(data))
      this._colorize()
    }
  }

  private _updateGradient(config: any) {
    this._palette = this._getColorPalette(config)
  }

  updateConfig(config: any) {
    if (config.gradient)
      this._updateGradient(config)

    this._setStyles(config)
  }

  setDimensions(width: number, height: number) {
    this._width = width
    this._height = height
    this.canvas.width = this.shadowCanvas.width = width
    this.canvas.height = this.shadowCanvas.height = height
  }

  private _clear() {
    this.shadowCtx.clearRect(0, 0, this._width, this._height)
    this.ctx.clearRect(0, 0, this._width, this._height)
  }

  private _setStyles(config: any) {
    this._blur = (config.blur === 0) ? 0 : (config.blur || config.defaultBlur)

    if (config.backgroundColor)
      this.canvas.style.backgroundColor = config.backgroundColor

    this._width = this.canvas.width = this.shadowCanvas.width = config.width || this._width
    this._height = this.canvas.height = this.shadowCanvas.height = config.height || this._height

    this._opacity = (config.opacity || 0) * 255
    this._maxOpacity = (config.maxOpacity || config.defaultMaxOpacity) * 255
    this._minOpacity = (config.minOpacity || config.defaultMinOpacity) * 255
    this._useGradientOpacity = !!config.useGradientOpacity
  }

  private _drawAlpha(data: any) {
    this._min = data.min
    this._max = data.max
    const data2 = data.data || []
    let dataLen = data2.length
    // on a point basis?
    const blur = 1 - this._blur

    while (dataLen--) {
      const point = data2[dataLen]

      const x = point.x
      const y = point.y
      const radius = point.radius
      // if value is bigger than max
      // use max as value
      const value = Math.min(point.value, this._max!)
      const rectX = x - radius
      const rectY = y - radius
      const shadowCtx = this.shadowCtx

      let tpl
      if (!this._templates[radius])
        this._templates[radius] = tpl = this._getPointTemplate(radius, blur)
      else
        tpl = this._templates[radius]

      // value from minimum / value range
      // => [0, 1]
      const templateAlpha = (value - this._min!) / (this._max! - this._min!)
      // this fixes #176: small values are not visible because globalAlpha < .01 cannot be read from imageData
      shadowCtx.globalAlpha = templateAlpha < 0.01 ? 0.01 : templateAlpha

      shadowCtx.drawImage(tpl, rectX, rectY)

      // update renderBoundaries
      if (rectX < this._renderBoundaries[0])
        this._renderBoundaries[0] = rectX

      if (rectY < this._renderBoundaries[1])
        this._renderBoundaries[1] = rectY

      if (rectX + 2 * radius > this._renderBoundaries[2])
        this._renderBoundaries[2] = rectX + 2 * radius

      if (rectY + 2 * radius > this._renderBoundaries[3])
        this._renderBoundaries[3] = rectY + 2 * radius
    }
  }

  private _colorize() {
    let x = this._renderBoundaries[0]
    let y = this._renderBoundaries[1]
    let width = this._renderBoundaries[2] - x
    let height = this._renderBoundaries[3] - y
    const maxWidth = this._width
    const maxHeight = this._height
    const opacity = this._opacity
    const maxOpacity = this._maxOpacity
    const minOpacity = this._minOpacity
    const useGradientOpacity = this._useGradientOpacity

    if (x < 0)
      x = 0

    if (y < 0)
      y = 0

    if (x + width > maxWidth)
      width = maxWidth - x

    if (y + height > maxHeight)
      height = maxHeight - y

    const img = this.shadowCtx.getImageData(x, y, width, height)
    const imgData = img.data
    const len = imgData.length
    const palette = this._palette

    for (let i = 3; i < len; i += 4) {
      const alpha = imgData[i]
      const offset = alpha * 4

      if (!offset)
        continue

      let finalAlpha
      if (opacity > 0) {
        finalAlpha = opacity
      }
      else {
        if (alpha < maxOpacity) {
          if (alpha < minOpacity)
            finalAlpha = minOpacity
          else
            finalAlpha = alpha
        }
        else {
          finalAlpha = maxOpacity
        }
      }

      imgData[i - 3] = palette[offset]
      imgData[i - 2] = palette[offset + 1]
      imgData[i - 1] = palette[offset + 2]
      imgData[i] = useGradientOpacity ? palette[offset + 3] : finalAlpha
    }

    // 创建新的ImageData对象
    const newImg = new ImageData(imgData, width, height)
    this.ctx.putImageData(newImg, x, y)

    this._renderBoundaries = [1000, 1000, 0, 0]
  }

  getValueAt(point: { x: number; y: number }) {
    const shadowCtx = this.shadowCtx
    const img = shadowCtx.getImageData(point.x, point.y, 1, 1)
    const data = img.data[3]
    const max = this._max
    const min = this._min

    const value = (Math.abs(max! - min!) * (data / 255)) >> 0
    return value
  }

  getDataURL() {
    return this.canvas.toDataURL()
  }
}

class Coordinator {
  private cStore: { [key: string]: ((data: any) => void)[] }

  constructor() {
    this.cStore = {}
  }

  on(evtName: string, callback: (data: any) => void, scope?: any) {
    const cStore = this.cStore

    if (!cStore[evtName])
      cStore[evtName] = []

    cStore[evtName].push((data: any) => {
      return callback.call(scope, data)
    })
  }

  emit(evtName: string, data: any) {
    const cStore = this.cStore
    if (cStore[evtName]) {
      const len = cStore[evtName].length
      for (let i = 0; i < len; i++) {
        const callback = cStore[evtName][i]
        callback(data)
      }
    }
  }
}

class Heatmap {
  private _config: any
  private _coordinator: Coordinator
  public _renderer: any
  private _store: Store

  constructor(config: any) {
    this._config = { ...HeatmapConfig, ...config }
    this._coordinator = new Coordinator()

    if (config.plugin) {
      const pluginToLoad = config.plugin
      if (!HeatmapConfig.plugins[pluginToLoad]) {
        throw new Error(`Plugin '${pluginToLoad}' not found. Maybe it was not registered.`)
      }
      else {
        const plugin = HeatmapConfig.plugins[pluginToLoad]
        // set plugin renderer and store
        const Renderer = plugin.renderer
        const Store = plugin.store
        this._renderer = new Renderer(config)
        this._store = new Store(config)
      }
    }
    else {
      this._renderer = new Canvas2dRenderer(config)
      this._store = new Store(config)
    }

    this._connect()
  }

  private _connect() {
    const renderer = this._renderer
    const coordinator = this._coordinator
    const store = this._store

    coordinator.on('renderpartial', renderer.renderPartial, renderer)
    coordinator.on('renderall', renderer.renderAll, renderer)
    coordinator.on('extremachange', (data: any) => {
      this._config.onExtremaChange
        && this._config.onExtremaChange({
          min: data.min,
          max: data.max,
          gradient: this._config.gradient || this._config.defaultGradient,
        })
    })
    store.setCoordinator(coordinator)
  }

  addData(...args: any[]) {
    if (args.length === 1)
      this._store.addData(args[0])
    else
      this._store.addData(...args as [any])

    return this
  }

  removeData(...args: any[]) {
    if (this._store.removeData)
      this._store.removeData()

    return this
  }

  setData(...args: any[]) {
    if (args.length === 1)
      this._store.setData(args[0])
    else
      this._store.setData(...args as [any])

    return this
  }

  setDataMax(...args: any[]) {
    if (args.length === 1)
      this._store.setDataMax(args[0])
    else
      this._store.setDataMax(...args as [any])

    return this
  }

  setDataMin(...args: any[]) {
    if (args.length === 1)
      this._store.setDataMin(args[0])
    else
      this._store.setDataMin(...args as [any])

    return this
  }

  configure(config: any) {
    this._config = { ...this._config, ...config }
    this._renderer.updateConfig(this._config)
    this._coordinator.emit('renderall', this._store.getInternalData())
    return this
  }

  repaint() {
    this._coordinator.emit('renderall', this._store.getInternalData())
    return this
  }

  getData() {
    return this._store.getData()
  }

  getDataURL() {
    return this._renderer.getDataURL()
  }

  getValueAt(point: { x: number; y: number }) {
    if (this._store.getValueAt)
      return this._store.getValueAt(point)
    else if (this._renderer.getValueAt)
      return this._renderer.getValueAt(point)
    else
      return null
  }
}

export default {
  create(config: any) {
    return new Heatmap(config)
  },
  register(pluginKey: string, plugin: any) {
    HeatmapConfig.plugins[pluginKey] = plugin
  },
}